/**
 * Customer roster with health signals.
 *
 * Health comes from `computeHealthSignals()` in @pazportal/types — the same
 * function the Trigger.dev recompute task uses — fed with real rows: open
 * tickets, tickets past their SLA deadline, the latest UniFi uptime sample
 * per location, open Auvik alerts (only when Auvik is connected), the
 * contract renewal date and deployment statuses.
 *
 * When `customer_health_scores` holds a row computed within the last two
 * hours, its stored signals are preferred and the row is labelled "scored";
 * otherwise the signals are computed client-side and labelled "computed
 * now". A signal with no data stays `null` — it is never rendered as a
 * healthy 20.
 */
import { useQuery } from '@tanstack/react-query';
import {
  computeHealthSignals,
  HEALTH_SCORE_DEGRADED_MIN,
  HEALTH_SCORE_HEALTHY_MIN,
  HEALTH_SIGNAL_KEYS,
  HEALTH_SIGNAL_MAX,
  type HealthSignalInput,
  type HealthSignalKey,
  type HealthSignalResult,
  type HealthStatus,
} from '@pazportal/types';
import { supabase } from '../lib/supabase';
import { daysUntil } from '../lib/formatters';
import { OPEN_TICKET_STATUSES } from './usePlatformMetrics';

/** A stored health row is trusted for this long before we recompute. */
export const STORED_HEALTH_FRESH_MS = 2 * 60 * 60 * 1000;
/** Uptime samples older than this are not "current" telemetry. */
export const UPTIME_SAMPLE_WINDOW_MS = 24 * 60 * 60 * 1000;

export interface CustomerHealthInputs extends HealthSignalInput {
  /** When the newest uptime sample used for `uptimePct24h` was taken. */
  uptimeSampledAt: string | null;
  /** Locations with a current uptime sample. */
  uptimeLocations: number;
  /** True when Auvik has a mapped tenant for this customer (alerts are meaningful). */
  auvikConnected: boolean;
}

export interface CustomerHealth {
  signals: HealthSignalResult;
  /** 'stored' = customer_health_scores row < 2h old; 'computed' = computed now. */
  source: 'stored' | 'computed';
  /** computed_at of the stored row when source === 'stored'. */
  scoredAt: string | null;
  inputs: CustomerHealthInputs;
}

export interface CustomerWithSignals {
  id: string;
  name: string;
  account_number: string;
  mrr_cents: number;
  sla_tier: string;
  status: string;
  contract_renewal_date: string | null;
  internal_notes: string | null;
  billing_cycle: string;
  primary_contact_id: string | null;
  zoho_account_id: string | null;
  is_zoho_synced: boolean;
  portal_active: boolean;
  created_at: string;
  updated_at: string;
  locationCount: number;
  deploymentCount: number;
  openTicketCount: number;
  breachedTicketCount: number;
  renewalDays: number | null;
  health: CustomerHealth;
}

interface StoredHealthRow {
  customer_id: string;
  score: number;
  tickets_signal: number | null;
  sla_signal: number | null;
  renewal_signal: number | null;
  mrr_signal: number | null;
  uptime_signal: number | null;
  computed_at: string;
}

/** Status bands for a stored row, aligned with computeHealthSignals(). */
function statusFromStored(row: StoredHealthRow): HealthStatus {
  const present = [row.tickets_signal, row.sla_signal, row.uptime_signal, row.renewal_signal, row.mrr_signal].filter(
    (v): v is number => v !== null,
  );
  if (present.length === 0) return 'unknown';
  if (row.sla_signal !== null && row.sla_signal < HEALTH_SIGNAL_MAX) return 'critical';
  if (row.score < HEALTH_SCORE_DEGRADED_MIN) return 'critical';
  if (row.score < HEALTH_SCORE_HEALTHY_MIN) return 'degraded';
  if (present.some((v) => v === 0)) return 'degraded';
  return 'healthy';
}

function signalsFromStored(row: StoredHealthRow): HealthSignalResult {
  const present = [row.tickets_signal, row.sla_signal, row.uptime_signal, row.renewal_signal, row.mrr_signal].filter(
    (v): v is number => v !== null,
  );
  return {
    tickets_signal: row.tickets_signal,
    sla_signal: row.sla_signal,
    uptime_signal: row.uptime_signal,
    renewal_signal: row.renewal_signal,
    mrr_signal: null,
    score: present.length === 0 ? null : row.score,
    coverage: present.length / HEALTH_SIGNAL_KEYS.length,
    status: statusFromStored(row),
  };
}

export function isStoredHealthFresh(computedAt: string | null | undefined, now = Date.now()): boolean {
  if (!computedAt) return false;
  const age = now - new Date(computedAt).getTime();
  return Number.isFinite(age) && age >= 0 && age < STORED_HEALTH_FRESH_MS;
}

/** Builds the health for one customer from its inputs and (optionally) a stored row. */
export function buildCustomerHealth(
  inputs: CustomerHealthInputs,
  stored: StoredHealthRow | null,
  now = Date.now(),
): CustomerHealth {
  if (stored && isStoredHealthFresh(stored.computed_at, now)) {
    return { signals: signalsFromStored(stored), source: 'stored', scoredAt: stored.computed_at, inputs };
  }
  return { signals: computeHealthSignals(inputs), source: 'computed', scoredAt: null, inputs };
}

// ─── Presentation helpers (shared by roster, dashboard, revenue, detail) ────

export const HEALTH_STATUS_LABEL: Record<HealthStatus, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  critical: 'Critical',
  unknown: 'No data',
};

/** Pill classes per status; unknown is deliberately grey, never green. */
export const HEALTH_STATUS_PILL: Record<HealthStatus, string> = {
  healthy: 'bg-success-soft text-success',
  degraded: 'bg-warning-soft text-warning',
  critical: 'bg-error-soft text-error',
  unknown: 'bg-surface-3 text-text-muted',
};

export const HEALTH_STATUS_BAR: Record<HealthStatus, string> = {
  healthy: 'bg-success',
  degraded: 'bg-warning',
  critical: 'bg-error',
  unknown: 'bg-surface-3',
};

export function formatHealthScore(score: number | null): string {
  return score === null ? '—' : String(score);
}

/** "3 of 5 signals" — mrr_signal is always null, so 4 of 5 is the ceiling. */
export function coverageLabel(coverage: number): string {
  const total = HEALTH_SIGNAL_KEYS.length;
  return `${Math.round(coverage * total)} of ${total} signals`;
}

export const SIGNAL_LABEL: Record<HealthSignalKey, string> = {
  tickets_signal: 'Tickets',
  sla_signal: 'SLA',
  uptime_signal: 'Uptime',
  renewal_signal: 'Renewal',
  mrr_signal: 'MRR',
};

export const SIGNAL_SHORT: Record<HealthSignalKey, string> = {
  tickets_signal: 'T',
  sla_signal: 'S',
  uptime_signal: 'U',
  renewal_signal: 'R',
  mrr_signal: 'M',
};

function formatAge(iso: string | null, now = Date.now()): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Plain-language detail for one signal, derived only from the inputs that fed it. */
export function describeSignal(key: HealthSignalKey, health: CustomerHealth): string {
  const { inputs } = health;
  switch (key) {
    case 'tickets_signal':
      return inputs.openTickets === null ? 'No ticket data' : `${inputs.openTickets} open`;
    case 'sla_signal':
      return inputs.breachedTickets === null
        ? 'No SLA data'
        : inputs.breachedTickets === 0
          ? 'No open tickets past SLA'
          : `${inputs.breachedTickets} past SLA deadline`;
    case 'uptime_signal': {
      const parts: string[] = [];
      if (inputs.uptimePct24h !== null) {
        parts.push(`${inputs.uptimePct24h.toFixed(1)}% devices online (UniFi, ${formatAge(inputs.uptimeSampledAt)})`);
      }
      if (inputs.openAlerts !== null) parts.push(`${inputs.openAlerts} open Auvik alert${inputs.openAlerts === 1 ? '' : 's'}`);
      if ((inputs.deploymentsTotal ?? 0) > 0) {
        parts.push(
          `${inputs.deploymentsTotal} deployment${inputs.deploymentsTotal === 1 ? '' : 's'}: ${inputs.deploymentsOffline ?? 0} offline, ${inputs.deploymentsDegraded ?? 0} degraded`,
        );
      }
      return parts.length ? parts.join(' · ') : 'No network telemetry yet';
    }
    case 'renewal_signal':
      return inputs.renewalDays === null
        ? 'No renewal date'
        : inputs.renewalDays < 0
          ? `Renewal ${Math.abs(inputs.renewalDays)} days overdue`
          : `${inputs.renewalDays} days to renewal`;
    case 'mrr_signal':
      return 'MRR history is not tracked yet';
  }
}

/** "Scored 12m ago" or "Computed now". */
export function healthSourceLabel(health: CustomerHealth, now = Date.now()): string {
  if (health.source === 'stored') return `Scored ${formatAge(health.scoredAt, now)}`;
  return 'Computed now';
}

/** Sort comparator: numeric scores ascending, unknown (null) last. */
export function compareScore(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

// ─── Query ───────────────────────────────────────────────────────────────────

export function useAllCustomersWithSignals() {
  return useQuery({
    queryKey: ['admin-customers-signals'],
    queryFn: async (): Promise<CustomerWithSignals[]> => {
      const now = Date.now();
      const since = new Date(now - UPTIME_SAMPLE_WINDOW_MS).toISOString();

      const [customersRes, healthRes, locationsRes, deploymentsRes, ticketsRes, samplesRes, alertsRes, tenantsRes] =
        await Promise.all([
          supabase
            .from('customers')
            .select(
              'id, name, account_number, mrr_cents, sla_tier, status, contract_renewal_date, internal_notes, billing_cycle, primary_contact_id, zoho_account_id, is_zoho_synced, portal_active, created_at, updated_at',
            )
            .is('deleted_at', null)
            .order('name'),
          supabase
            .from('customer_health_scores')
            .select('customer_id, score, tickets_signal, sla_signal, renewal_signal, mrr_signal, uptime_signal, computed_at'),
          supabase.from('locations').select('id, customer_id').is('deleted_at', null),
          supabase
            .from('deployments')
            .select('id, status, locations!inner(customer_id)')
            .neq('status', 'decommissioned'),
          supabase
            .from('tickets')
            .select('id, customer_id, status, sla_deadline')
            .in('status', [...OPEN_TICKET_STATUSES]),
          supabase
            .from('location_uptime_samples')
            .select('customer_id, location_id, sampled_at, online_pct, total_count')
            .gte('sampled_at', since)
            .order('sampled_at', { ascending: false })
            .limit(3000),
          supabase
            .from('network_alerts')
            .select('customer_id')
            .is('resolved_at', null)
            .or('status.is.null,and(status.neq.cleared,status.neq.resolved)'),
          supabase.from('auvik_tenant_map').select('customer_id').not('customer_id', 'is', null),
        ]);

      for (const res of [customersRes, healthRes, locationsRes, deploymentsRes, ticketsRes, samplesRes, alertsRes, tenantsRes]) {
        if (res.error) throw res.error;
      }

      const healthMap = new Map<string, StoredHealthRow>();
      for (const h of (healthRes.data ?? []) as StoredHealthRow[]) healthMap.set(h.customer_id, h);

      const locationCounts = new Map<string, number>();
      for (const loc of locationsRes.data ?? []) {
        locationCounts.set(loc.customer_id, (locationCounts.get(loc.customer_id) ?? 0) + 1);
      }

      const deploymentsByCustomer = new Map<string, { total: number; offline: number; degraded: number }>();
      for (const dep of deploymentsRes.data ?? []) {
        const custId = (dep.locations as unknown as { customer_id: string } | null)?.customer_id;
        if (!custId) continue;
        const entry = deploymentsByCustomer.get(custId) ?? { total: 0, offline: 0, degraded: 0 };
        entry.total += 1;
        if (dep.status === 'offline') entry.offline += 1;
        else if (dep.status === 'degraded' || dep.status === 'installing') entry.degraded += 1;
        deploymentsByCustomer.set(custId, entry);
      }

      const ticketCounts = new Map<string, { open: number; breached: number }>();
      for (const t of ticketsRes.data ?? []) {
        const entry = ticketCounts.get(t.customer_id) ?? { open: 0, breached: 0 };
        entry.open += 1;
        if (t.sla_deadline && new Date(t.sla_deadline).getTime() < now) entry.breached += 1;
        ticketCounts.set(t.customer_id, entry);
      }

      // Latest sample per location (rows arrive newest first), then a
      // device-weighted mean per customer.
      const latestByLocation = new Map<string, { customer_id: string; sampled_at: string; online_pct: number; total_count: number }>();
      for (const s of samplesRes.data ?? []) {
        if (!latestByLocation.has(s.location_id)) {
          latestByLocation.set(s.location_id, {
            customer_id: s.customer_id,
            sampled_at: s.sampled_at,
            online_pct: Number(s.online_pct),
            total_count: s.total_count,
          });
        }
      }
      const uptimeByCustomer = new Map<string, { weighted: number; weight: number; locations: number; latest: string }>();
      for (const sample of latestByLocation.values()) {
        const entry = uptimeByCustomer.get(sample.customer_id) ?? { weighted: 0, weight: 0, locations: 0, latest: sample.sampled_at };
        const weight = Math.max(sample.total_count, 1);
        entry.weighted += sample.online_pct * weight;
        entry.weight += weight;
        entry.locations += 1;
        if (sample.sampled_at > entry.latest) entry.latest = sample.sampled_at;
        uptimeByCustomer.set(sample.customer_id, entry);
      }

      const alertCounts = new Map<string, number>();
      for (const a of alertsRes.data ?? []) {
        if (a.customer_id) alertCounts.set(a.customer_id, (alertCounts.get(a.customer_id) ?? 0) + 1);
      }
      const auvikCustomers = new Set<string>();
      for (const t of tenantsRes.data ?? []) if (t.customer_id) auvikCustomers.add(t.customer_id);

      return (customersRes.data ?? []).map((c): CustomerWithSignals => {
        const tickets = ticketCounts.get(c.id) ?? { open: 0, breached: 0 };
        const deps = deploymentsByCustomer.get(c.id) ?? { total: 0, offline: 0, degraded: 0 };
        const uptime = uptimeByCustomer.get(c.id) ?? null;
        const auvikConnected = auvikCustomers.has(c.id);
        const renewalDays = daysUntil(c.contract_renewal_date);

        const inputs: CustomerHealthInputs = {
          openTickets: tickets.open,
          breachedTickets: tickets.breached,
          uptimePct24h: uptime && uptime.weight > 0 ? Math.round((uptime.weighted / uptime.weight) * 100) / 100 : null,
          uptimeSampledAt: uptime?.latest ?? null,
          uptimeLocations: uptime?.locations ?? 0,
          openAlerts: auvikConnected ? (alertCounts.get(c.id) ?? 0) : null,
          auvikConnected,
          renewalDays,
          deploymentsOffline: deps.total > 0 ? deps.offline : null,
          deploymentsDegraded: deps.total > 0 ? deps.degraded : null,
          deploymentsTotal: deps.total,
        };

        return {
          id: c.id,
          name: c.name,
          account_number: c.account_number,
          mrr_cents: c.mrr_cents ?? 0,
          sla_tier: c.sla_tier ?? 'standard',
          status: c.status ?? 'active',
          contract_renewal_date: c.contract_renewal_date,
          internal_notes: c.internal_notes,
          billing_cycle: c.billing_cycle ?? 'monthly',
          primary_contact_id: c.primary_contact_id,
          zoho_account_id: c.zoho_account_id,
          is_zoho_synced: Boolean(c.is_zoho_synced),
          portal_active: Boolean(c.portal_active),
          created_at: c.created_at,
          updated_at: c.updated_at,
          locationCount: locationCounts.get(c.id) ?? 0,
          deploymentCount: deps.total,
          openTicketCount: tickets.open,
          breachedTicketCount: tickets.breached,
          renewalDays,
          health: buildCustomerHealth(inputs, healthMap.get(c.id) ?? null, now),
        };
      });
    },
    staleTime: 2 * 60 * 1000,
  });
}
